/** @jsxImportSource @emotion/react */
import { Header, useSession } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Icon,
  Input,
  Loader,
  Select,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { CaseCustomerType } from 'api/useCasesApi';
import { CreateCaseModal, KycDocumentImages } from 'components';
import { FCUCards } from 'components/FCUCards';
import { getKycDocumentImage, getKycDocumentImages } from 'tabs/AppUsers/api';
import { updateUser, searchAffiliateUsers } from '../../api';
import { AccountStatus, getLabel } from '../../types';
import { AffiliateOnboardingHistoryTable } from './AffiliateOnboardingHistoryTable';
import { AffiliateOnboardingTable } from './AffiliateOnboardingTable';
import { AffiliateTransactions } from './AffiliateTransactions';
import { AffiliateVerificationTable } from './AffiliateVerificationTable';

const parseIntOrZeroToString = (value?: string | null) =>
  (parseInt(value ?? '0') || 0).toString();

type Tab = 'details' | 'transactions' | 'onboardingHistory';

export const AffiliateUserDetails = () => {
  const session = useSession();
  const { spacing, palette } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const tabParam = searchParams.get('tab');
    return tabParam &&
      ['details', 'transactions', 'onboardingHistory'].includes(tabParam)
      ? (tabParam as Tab)
      : 'details';
  });

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (
      tabParam &&
      ['details', 'transactions', 'onboardingHistory'].includes(tabParam)
    ) {
      setTab(tabParam as Tab);
    }
  }, [searchParams]);

  const { userId } = useParams();
  if (!userId) {
    throw Error('Missing userId param');
  }

  const hasEditPermission =
    session.authenticated && session.permissions.includes('edit_qb_users');

  const [riskScoreAdjustment, setRiskScoreAdjustment] = useState<string | null>(
    null
  );
  const [status, setStatus] = useState<AccountStatus>(AccountStatus.ACTIVE);
  const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
  const [{ value: user, loading: searchLoading }, search] =
    useAsyncFn(async () => {
      if (userId) {
        const { users } = await searchAffiliateUsers({
          filters: { userId },
          pagination: { page: 1, size: 1 },
        });
        if (users.length > 0) {
          setStatus(users[0].status);
          setRiskScoreAdjustment(
            parseIntOrZeroToString(users[0].riskScoreAdjustment)
          );
          return users[0];
        }
      }
    }, [userId]);

  const [{ loading: updateLoading }, doUpdate] = useAsyncFn(
    async (newStatus: AccountStatus, newRiskScoreAdjustment: string | null) => {
      if (!user || !user.externalReference) return;

      const updatedRiskScoreAdjustment =
        parseIntOrZeroToString(user.riskScoreAdjustment) !==
        parseIntOrZeroToString(newRiskScoreAdjustment)
          ? parseInt(parseIntOrZeroToString(newRiskScoreAdjustment))
          : undefined;

      const updatedStatus = user.status !== newStatus ? newStatus : undefined;

      if (
        updatedRiskScoreAdjustment === undefined &&
        updatedStatus === undefined
      ) {
        return;
      }

      const response = await updateUser({
        userReference: user.externalReference,
        status: newStatus,
        riskScoreAdjustment: updatedRiskScoreAdjustment,
      });
      await search();
      return response;
    },
    [user]
  );

  const handleStatusChange = (newStatus: AccountStatus) => {
    setStatus(newStatus);
    void doUpdate(newStatus, riskScoreAdjustment);
  };

  const handleRiskScoreChange = (newRiskScore: string) => {
    setRiskScoreAdjustment(newRiskScore);
  };

  const handleRiskScoreBlur = () => {
    void doUpdate(status, riskScoreAdjustment);
  };

  useEffect(() => {
    if (!user) {
      void search();
    }
  }, [user, search]);

  if (searchLoading) {
    return <Loader />;
  }
  return (
    <>
      <Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            gap: spacing.xxs,
            alignItems: 'center',
          }}
        >
          <div
            css={{ display: 'flex', gap: spacing.xxxs, alignItems: 'center' }}
          >
            <div
              css={{
                cursor: 'pointer',
                ':hover': {
                  textDecoration: 'underline',
                  textDecorationColor: palette.grey.x300,
                },
              }}
              onClick={() => navigate('/users/affiliate')}
            >
              <Typography variant="subtitle" color={palette.grey.x300}>
                Affiliate Users
              </Typography>
            </div>
            <div
              css={{
                transform: 'rotate(180deg)',
                svg: {
                  fill: 'none',
                  stroke: palette.grey.x300,
                },
              }}
            >
              <Icon name="chevron" />
            </div>
            <Typography variant="subtitle">{userId}</Typography>
          </div>
        </div>
      </Header>
      <div css={{ marginBottom: spacing.xxs }}>
        <ToggleButtonGroup
          onChange={(option) => {
            setTab(option as Tab);
            setSearchParams({ tab: option });
          }}
          selected={tab}
          toggleOptions={[
            { label: 'Details', value: 'details' },
            { label: 'Onboarding History', value: 'onboardingHistory' },
            { label: 'Transactions', value: 'transactions' },
          ]}
        />
      </div>
      {tab === 'details' && (
        <div>
          <Card
            css={{
              display: 'grid',
              gap: spacing.xxxs,
              gridTemplateColumns: '1fr 1fr',
            }}
          >
            {user && (
              <>
                <Select
                  label={getLabel('status')}
                  options={Object.keys(AccountStatus).map((s) => ({
                    label: s,
                    value: s,
                  }))}
                  values={[status]}
                  onSelect={(s) => handleStatusChange(s[0] as AccountStatus)}
                  disabled={!hasEditPermission || updateLoading}
                />
                <div
                  css={{
                    'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button':
                      {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                    'input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                  }}
                >
                  <Input
                    type="number"
                    label={getLabel('riskScoreAdjustment')}
                    value={riskScoreAdjustment ?? ''}
                    onChange={(e) => handleRiskScoreChange(e.target.value)}
                    onBlur={handleRiskScoreBlur}
                    disabled={!hasEditPermission || updateLoading}
                  />
                </div>
              </>
            )}
            {user &&
              Object.entries(user)
                .filter(
                  ([k]) =>
                    ![
                      'status',
                      'riskScoreAdjustment',
                      'sourceOfFundsVerificationStatus',
                    ].includes(k)
                )
                .sort()
                .map(([k, v]) => {
                  return (
                    <div
                      css={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: palette.grey.default,
                        },
                      }}
                      key={k}
                    >
                      <Input
                        type={
                          v != null && typeof v === 'string' && v.length > 100
                            ? 'textarea'
                            : 'text'
                        }
                        label={getLabel(k)}
                        value={
                          v == null
                            ? '-'
                            : typeof v === 'string'
                            ? v
                            : JSON.stringify(v)
                        }
                        onChange={() => ({})}
                        disabled
                      />
                    </div>
                  );
                })}
          </Card>
          {user?.onboarding && (
            <div css={{ marginTop: spacing.xxs }}>
              <AffiliateOnboardingTable onboarding={user.onboarding} />
            </div>
          )}
          {user?.verification && (
            <div css={{ marginTop: spacing.xxs }}>
              <AffiliateVerificationTable verification={user.verification} />
            </div>
          )}

          {user && <FCUCards customerId={userId} customerType={'AFFILIATE'} />}
        </div>
      )}
      {tab === 'onboardingHistory' && (
        <>
          {user && <AffiliateOnboardingHistoryTable userId={user.uuid || ''} />}
        </>
      )}
      {tab === 'transactions' && (
        <>
          {user && (
            <AffiliateTransactions key={user.uuid} userId={user.uuid || ''} />
          )}
        </>
      )}
      <KycDocumentImages
        userId={userId}
        getKycDocumentImage={getKycDocumentImage}
        getKycDocumentImages={getKycDocumentImages}
      />

      {showCreateCaseModal && user && (
        <CreateCaseModal
          customerId={userId}
          customerType={user.userType as CaseCustomerType}
          onClose={() => setShowCreateCaseModal(false)}
        />
      )}
    </>
  );
};
